import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SendIcon from '@mui/icons-material/Send';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment-jalaali';
import api from '../../config/axiosConfig';
import useFetch from '../../hooks/useFetch';
import usePolling from '../../hooks/usePolling';
import { useAuth } from '../../context/AuthContext';
import RTL from '../../utils/RTL';
import { toEnglishDigits, toPersianDigits } from '../../utils/convertNumbers';
import { formatJalaliDate } from '../../utils/formatJalaliDate';
import { API } from '../../utils/apiRoutes';
import FloatingSnackbar from '../common/FloatingSnackbar';

const POLL_INTERVAL_MS = 15000;
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const IMAGE_TOO_LARGE_MESSAGE = 'حجم تصویر نباید بیشتر از ۵ مگابایت باشد.';

// Two inbox segments instead of one chip per status: "open" is the queue staff
// actually works (escalated); everything else is lookup/history.
const INBOX_SEGMENTS = [
    { value: 'open', label: 'باز', statuses: 'escalated' },
    { value: 'archive', label: 'بایگانی', statuses: 'resolved,closed,ai_handling' },
];

const STATUS_LABELS = {
    ai_handling: 'هوش مصنوعی',
    escalated: 'ارجاع‌شده',
    resolved: 'حل‌شده',
    closed: 'بسته‌شده',
};

function formatTime(value) {
    if (!value) return '';
    const m = moment(value);
    return m.isValid() ? toPersianDigits(m.format('HH:mm')) : '';
}

function dayKey(value) {
    const m = moment(value);
    return m.isValid() ? m.format('jYYYY/jMM/jDD') : '';
}

function formatDayLabel(value) {
    const m = moment(value);
    if (!m.isValid()) return '';
    const sameYear = m.jYear() === moment().jYear();
    return toPersianDigits(m.format(sameYear ? 'dddd jD jMMMM' : 'jD jMMMM jYYYY'));
}

function formatRelativeTime(value) {
    const m = moment(value);
    if (!m.isValid()) return '';
    const minutes = moment().diff(m, 'minutes');
    if (minutes < 1) return 'لحظاتی پیش';
    if (minutes < 60) return `${toPersianDigits(String(minutes))} دقیقه پیش`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${toPersianDigits(String(hours))} ساعت پیش`;
    if (hours < 48) return 'دیروز';
    return toPersianDigits(formatJalaliDate(value));
}

function MessageBubble({ message, showStaffChip }) {
    const isUser = message.role === 'user';
    const isStaff = message.role === 'staff';

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isUser ? 'flex-start' : 'flex-end',
                mb: 0.75,
            }}
        >
            {isStaff && showStaffChip && (
                <Chip
                    size="small"
                    icon={<SupportAgentIcon />}
                    label="پشتیبان انسانی"
                    color="secondary"
                    sx={{ mb: 0.25, height: 20, '& .MuiChip-label': { fontSize: '0.65rem' } }}
                />
            )}
            <Paper
                elevation={0}
                sx={(t) => ({
                    px: 1.5,
                    py: 0.75,
                    maxWidth: { xs: '85%', sm: '70%' },
                    bgcolor: isUser
                        ? t.palette.mode === 'dark'
                            ? t.palette.grey[800]
                            : t.palette.grey[200]
                        : isStaff
                            ? t.palette.secondary.light
                            : t.palette.background.neutral,
                    color: isUser
                        ? t.palette.text.primary
                        : t.palette.getContrastText(
                              isStaff ? t.palette.secondary.light : t.palette.background.neutral,
                          ),
                    borderRadius: 2,
                })}
            >
                {message.image && (
                    <Box
                        component="img"
                        src={message.image}
                        alt="تصویر پیوست"
                        onClick={() => window.open(message.image, '_blank', 'noopener')}
                        sx={{
                            display: 'block',
                            maxWidth: '100%',
                            maxHeight: 240,
                            borderRadius: 1.5,
                            mb: message.content ? 1 : 0,
                            cursor: 'pointer',
                        }}
                    />
                )}
                {message.content && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {message.content}
                    </Typography>
                )}
                <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 0.25, fontSize: '0.65rem', opacity: 0.65 }}
                >
                    {formatTime(message.created_at)}
                </Typography>
            </Paper>
        </Box>
    );
}

function DaySeparator({ date }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
            <Chip
                size="small"
                variant="outlined"
                label={formatDayLabel(date)}
                sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.7rem' } }}
            />
        </Box>
    );
}

function ConversationThread({ messages }) {
    let previousDay = null;
    let previousRole = null;

    return messages.map((message) => {
        const day = dayKey(message.created_at);
        const showDay = day && day !== previousDay;
        const showStaffChip = message.role === 'staff' && previousRole !== 'staff';
        previousDay = day;
        previousRole = message.role;

        return (
            <React.Fragment key={message.id}>
                {showDay && <DaySeparator date={message.created_at} />}
                <MessageBubble message={message} showStaffChip={showStaffChip} />
            </React.Fragment>
        );
    });
}

function StaffSupportPanel() {
    const { user } = useAuth();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [segment, setSegment] = useState('open');
    const [selectedId, setSelectedId] = useState(null);
    const [draft, setDraft] = useState('');
    const [attachment, setAttachment] = useState(null);
    const [sending, setSending] = useState(false);
    const [resolving, setResolving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const threadScrollRef = useRef(null);

    const isAllowed = useMemo(() => {
        return Boolean(user?.is_superuser || user?.is_support_staff);
    }, [user]);

    const activeSegment = INBOX_SEGMENTS.find((item) => item.value === segment) || INBOX_SEGMENTS[0];
    const listParams = useMemo(() => ({ status: activeSegment.statuses }), [activeSegment.statuses]);
    const {
        data: listData,
        loading: listLoading,
        refetch: refetchList,
    } = useFetch(API.aiStaffConversations, {
        params: listParams,
        skip: !isAllowed,
    });

    const detailUrl = selectedId ? `${API.aiStaffConversations}${selectedId}/` : null;
    const {
        data: activeConversation,
        loading: detailLoading,
        refetch: refetchDetail,
    } = useFetch(detailUrl, { skip: !selectedId || !isAllowed });

    const conversations = useMemo(() => listData?.results || listData || [], [listData]);
    const messages = activeConversation?.messages || [];

    // On desktop the thread pane should never sit empty: open the newest
    // conversation of the active segment. On mobile the list is the landing
    // view, so no auto-select.
    useEffect(() => {
        if (isDesktop && !selectedId && !listLoading && conversations.length > 0) {
            setSelectedId(conversations[0].id);
        }
    }, [isDesktop, selectedId, listLoading, conversations]);

    // Keep the newest message in view.
    useEffect(() => {
        const el = threadScrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages.length, selectedId]);

    // Live-ish updates: quietly refresh the inbox (new escalations, new user
    // replies float up) and the open thread without flashing spinners.
    usePolling(
        () => {
            refetchList({ silent: true });
            if (selectedId) {
                refetchDetail({ silent: true });
            }
        },
        POLL_INTERVAL_MS,
        isAllowed,
    );

    const attachmentPreview = useMemo(
        () => (attachment ? URL.createObjectURL(attachment) : null),
        [attachment],
    );
    useEffect(() => {
        return () => {
            if (attachmentPreview) URL.revokeObjectURL(attachmentPreview);
        };
    }, [attachmentPreview]);

    const handleAttachmentChange = useCallback((event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            setSnackbar({ open: true, message: IMAGE_TOO_LARGE_MESSAGE, severity: 'error' });
            return;
        }
        setAttachment(file);
    }, []);

    const handleReply = useCallback(async () => {
        const message = draft.trim();
        if ((!message && !attachment) || !selectedId || sending) return;

        setSending(true);
        try {
            let payload = { message: toEnglishDigits(message) };
            if (attachment) {
                payload = new FormData();
                payload.append('message', toEnglishDigits(message));
                payload.append('image', attachment);
            }
            await api.post(
                `${API.aiStaffConversations}${selectedId}/reply/`,
                payload,
            );
            setDraft('');
            setAttachment(null);
            await refetchDetail();
            await refetchList();
            setSnackbar({
                open: true,
                message: 'پاسخ ثبت شد.',
                severity: 'success',
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: err?.response?.data?.detail || 'خطا در ارسال پاسخ',
                severity: 'error',
            });
        } finally {
            setSending(false);
        }
    }, [draft, attachment, selectedId, sending, refetchDetail, refetchList]);

    const handleResolve = useCallback(async () => {
        if (!selectedId || resolving) return;

        setResolving(true);
        try {
            await api.post(`${API.aiStaffConversations}${selectedId}/resolve/`);
            await refetchDetail();
            await refetchList();
            window.dispatchEvent(new Event('support-badge-sync'));
            setSnackbar({
                open: true,
                message: 'گفتگو به عنوان حل‌شده علامت‌گذاری شد.',
                severity: 'success',
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: err?.response?.data?.detail || 'خطا در به‌روزرسانی وضعیت',
                severity: 'error',
            });
        } finally {
            setResolving(false);
        }
    }, [selectedId, resolving, refetchDetail, refetchList]);

    if (!isAllowed) {
        return (
            <RTL>
                <Box sx={{ p: 3 }}>
                    <Alert severity="warning">شما دسترسی به پنل پشتیبانی گفتگوها را ندارید.</Alert>
                </Box>
            </RTL>
        );
    }

    const showEscalationReason =
        activeConversation?.status === 'escalated' && activeConversation?.escalation_reason;

    return (
        <RTL>
            <Box sx={{ p: { xs: 1.5, md: 3 }, height: '100%' }}>
                <Paper sx={{ height: '75vh', minHeight: 480, display: 'flex', overflow: 'hidden', p: 0 }}>
                    <Box
                        sx={{
                            width: { xs: '100%', md: 260 },
                            borderLeft: { md: 1 },
                            borderColor: { md: 'divider' },
                            display: { xs: selectedId ? 'none' : 'flex', md: 'flex' },
                            flexDirection: 'column',
                            flexShrink: 0,
                        }}
                    >
                        <Box sx={{ px: 2, pt: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }} noWrap>
                                گفتگوها
                            </Typography>
                            {INBOX_SEGMENTS.map((item) => (
                                <Chip
                                    key={item.value}
                                    label={item.label}
                                    clickable
                                    size="small"
                                    color={segment === item.value ? 'primary' : 'default'}
                                    onClick={() => {
                                        if (segment === item.value) return;
                                        setSegment(item.value);
                                        setSelectedId(null);
                                    }}
                                />
                            ))}
                        </Box>
                        <Divider />
                        {listLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : (
                            <List dense sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
                                {conversations.length === 0 ? (
                                    <Typography align="center" variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                        {segment === 'open'
                                            ? 'گفتگوی بازی وجود ندارد.'
                                            : 'گفتگویی در بایگانی نیست.'}
                                    </Typography>
                                ) : (
                                    conversations.map((conversation) => {
                                        const who =
                                            conversation.user_name || conversation.user_email || '';
                                        const parts = [who, formatRelativeTime(conversation.updated_at)];
                                        if (segment === 'archive') {
                                            parts.push(
                                                STATUS_LABELS[conversation.status] || conversation.status,
                                            );
                                        }
                                        return (
                                            <ListItemButton
                                                key={conversation.id}
                                                selected={selectedId === conversation.id}
                                                onClick={() => setSelectedId(conversation.id)}
                                            >
                                                <ListItemText
                                                    primary={conversation.title || 'گفتگوی بدون عنوان'}
                                                    secondary={parts.filter(Boolean).join(' · ')}
                                                    primaryTypographyProps={{ noWrap: true }}
                                                    secondaryTypographyProps={{ noWrap: true }}
                                                />
                                            </ListItemButton>
                                        );
                                    })
                                )}
                            </List>
                        )}
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            display: { xs: selectedId ? 'flex' : 'none', md: 'flex' },
                            flexDirection: 'column',
                            minWidth: 0,
                        }}
                    >
                        <Box
                            sx={{
                                px: 2,
                                py: 1.25,
                                borderBottom: 1,
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={() => setSelectedId(null)}
                                aria-label="بازگشت به فهرست"
                                sx={{ display: { md: 'none' } }}
                            >
                                <ArrowForwardIcon fontSize="small" />
                            </IconButton>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                                    {activeConversation?.title || 'انتخاب گفتگو'}
                                </Typography>
                                {activeConversation && (
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {activeConversation.user_name || activeConversation.user_email}
                                        {activeConversation.institute_name
                                            ? ` — ${activeConversation.institute_name}`
                                            : ''}
                                    </Typography>
                                )}
                            </Box>
                            {selectedId && activeConversation?.status !== 'resolved' && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    startIcon={<CheckCircleOutlineIcon />}
                                    onClick={handleResolve}
                                    disabled={resolving}
                                >
                                    {resolving ? '...' : 'حل شد'}
                                </Button>
                            )}
                        </Box>

                        {showEscalationReason && (
                            <Box
                                sx={{
                                    px: 2,
                                    py: 0.75,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                }}
                            >
                                <InfoOutlinedIcon sx={{ fontSize: 16 }} color="warning" />
                                <Tooltip title={activeConversation.escalation_reason} arrow>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        دلیل ارجاع: {activeConversation.escalation_reason}
                                    </Typography>
                                </Tooltip>
                            </Box>
                        )}

                        <Box
                            ref={threadScrollRef}
                            sx={{
                                flex: 1,
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {!selectedId ? (
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography color="text.secondary">
                                        {conversations.length === 0 && !listLoading
                                            ? 'گفتگویی برای نمایش نیست.'
                                            : 'یک گفتگو را از فهرست انتخاب کنید.'}
                                    </Typography>
                                </Box>
                            ) : detailLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        mt: 'auto',
                                        width: '100%',
                                        maxWidth: 720,
                                        mx: 'auto',
                                        px: 2,
                                        py: 1.5,
                                    }}
                                >
                                    <ConversationThread messages={messages} />
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                            {attachment && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Box
                                        component="img"
                                        src={attachmentPreview}
                                        alt="پیش‌نمایش تصویر"
                                        sx={{ height: 56, borderRadius: 1, border: 1, borderColor: 'divider' }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }} noWrap>
                                        {attachment.name}
                                    </Typography>
                                    <IconButton size="small" onClick={() => setAttachment(null)} aria-label="حذف تصویر">
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                            {/* RTL row: first child renders on the right, so the send
                                button leads, matching the user-side SupportChat composer. */}
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <IconButton
                                    color="primary"
                                    onClick={handleReply}
                                    disabled={!selectedId || sending || (!draft.trim() && !attachment)}
                                    aria-label="ارسال پاسخ"
                                >
                                    {sending ? <CircularProgress size={22} /> : <SendIcon />}
                                </IconButton>
                                <IconButton
                                    color="primary"
                                    component="label"
                                    disabled={!selectedId || sending}
                                    aria-label="پیوست تصویر"
                                    title="پیوست تصویر"
                                >
                                    <ImageOutlinedIcon />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={handleAttachmentChange}
                                    />
                                </IconButton>
                                <TextField
                                    fullWidth
                                    multiline
                                    maxRows={4}
                                    placeholder="پاسخ پشتیبان..."
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    disabled={!selectedId || sending}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleReply();
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Paper>

                <FloatingSnackbar
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                />
            </Box>
        </RTL>
    );
}

export default StaffSupportPanel;
